import { Component, computed, ElementRef, inject, input, linkedSignal, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import {
    choosePeriod,
    customPeriodLimits,
    localIsoDateOrNull,
    parseLocalIsoDate,
    PERIOD_CHOICES,
    PeriodChoice,
    PeriodSelection,
    samePeriodSelection,
    withCustomFrom,
    withCustomTo
} from '../../utils/period-selection';

let nextId = 0;

/**
 * The period a list is narrowed to: a preset, or days of the user's own under "Personalizado".
 * A typed date only counts once it is settled (picked, Enter, or the field left): PrimeNG reports every
 * half-typed keystroke as an empty date, which would reload the list with that end open. For the same reason an
 * end only opens when its field is really empty: text PrimeNG took no date from puts the applied day back.
 */
@Component({
    selector: 'app-period-filter',
    imports: [FormsModule, SelectModule, DatePickerModule],
    host: { '[class.period-filter--custom]': "value().choice === 'CUSTOM'" },
    template: `
        <p-select class="period-filter__select" [options]="choices" optionLabel="label" optionValue="value"
            [ariaLabel]="ariaLabel()" [ngModel]="value().choice" (ngModelChange)="choose($event)">
            <ng-template #selectedItem let-option>
                <span class="period-filter__value">
                    <i class="pi pi-calendar period-filter__icon" aria-hidden="true"></i>
                    {{ option.label }}
                </span>
            </ng-template>
        </p-select>

        @if (value().choice === 'CUSTOM') {
            <div class="period-filter__range" role="group" aria-label="Período personalizado">
                <div class="period-filter__field">
                    <label class="period-filter__label" [for]="fromId">De</label>
                    <p-datepicker class="period-filter__date" [inputId]="fromId" dateFormat="dd/mm/yy"
                        placeholder="dd/mm/aaaa" [showIcon]="true" iconDisplay="input" [showButtonBar]="true"
                        [maxDate]="limits().fromMax" [ngModel]="fromDraft()" (ngModelChange)="fromDraft.set($event)"
                        (onSelect)="settleFrom()" (onClearClick)="settleFrom()" (onBlur)="settleFrom()"
                        (keydown.enter)="settleFrom()" />
                </div>
                <div class="period-filter__field">
                    <label class="period-filter__label" [for]="toId">Até</label>
                    <p-datepicker class="period-filter__date" [inputId]="toId" dateFormat="dd/mm/yy"
                        placeholder="dd/mm/aaaa" [showIcon]="true" iconDisplay="input" [showButtonBar]="true"
                        [minDate]="limits().toMin" [maxDate]="limits().toMax" [ngModel]="toDraft()"
                        (ngModelChange)="toDraft.set($event)" (onSelect)="settleTo()" (onClearClick)="settleTo()"
                        (onBlur)="settleTo()" (keydown.enter)="settleTo()" />
                </div>
            </div>
        }
    `,
    styles: `
        :host {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.75rem;
            max-width: 100%;
        }

        /* Wide enough for the longest label, so picking another period doesn't shift what sits beside it. */
        .period-filter__select {
            min-width: 12rem;
        }

        .period-filter__value {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
        }

        .period-filter__icon {
            color: var(--p-text-muted-color);
        }

        .period-filter__range {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.75rem;
        }

        .period-filter__field {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        .period-filter__label {
            color: var(--p-text-muted-color);
            font-size: 0.8125rem;
        }

        /* dd/mm/aaaa beside the calendar icon; a fixed width keeps Até still while De is typed. */
        .period-filter__date {
            width: 9.5rem;
        }

        /* On a phone the dates take a row of their own and share it. */
        @media (max-width: 575.98px) {
            :host(.period-filter--custom) {
                flex: 1 1 100%;
            }

            :host(.period-filter--custom) .period-filter__range {
                flex: 1 1 100%;
                flex-wrap: nowrap;
            }

            .period-filter__field {
                flex: 1 1 0;
                min-width: 0;
            }

            .period-filter__date {
                flex: 1 1 auto;
                width: auto;
                min-width: 0;
            }
        }
    `
})
export class PeriodFilterComponent {
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    readonly value = model.required<PeriodSelection>();
    /** Names the select. */
    readonly ariaLabel = input('Período');
    /** The last day either date can take: today, since the lists filter by when something happened. `null` lifts it. */
    readonly maxDate = input<Date | null>(new Date());

    /** A copy: p-select types its options as a mutable array. */
    protected readonly choices = [...PERIOD_CHOICES];
    private readonly id = nextId++;
    protected readonly fromId = `period-filter-${this.id}-from`;
    protected readonly toId = `period-filter-${this.id}-to`;

    /** What each field holds while it is being edited; back to the value whenever the value changes. */
    protected readonly fromDraft = linkedSignal<Date | null>(() => parseLocalIsoDate(this.value().from));
    protected readonly toDraft = linkedSignal<Date | null>(() => parseLocalIsoDate(this.value().to));
    protected readonly limits = computed(() => customPeriodLimits(this.fromDraft(), this.toDraft(), this.maxDate()));

    protected choose(choice: PeriodChoice): void {
        this.update(choosePeriod(this.value(), choice, new Date()));
    }

    protected settleFrom(): void {
        if (this.rejected(this.fromId, this.fromDraft())) {
            this.fromDraft.set(parseLocalIsoDate(this.value().from));
        } else {
            this.update(withCustomFrom(this.value(), localIsoDateOrNull(this.fromDraft())));
        }
    }

    protected settleTo(): void {
        if (this.rejected(this.toId, this.toDraft())) {
            this.toDraft.set(parseLocalIsoDate(this.value().to));
        } else {
            this.update(withCustomTo(this.value(), localIsoDateOrNull(this.toDraft())));
        }
    }

    private update(next: PeriodSelection): void {
        if (!samePeriodSelection(this.value(), next)) {
            this.value.set(next);
        }
    }

    /**
     * Whether the field holds text PrimeNG took no date from: half typed, or past a limit. It keeps no date for
     * such text and only erases it after onBlur (Limpar empties it before onClearClick), so reading the field is
     * what tells a rejected date from an emptied one. Putting the draft back also writes the applied day into it.
     */
    private rejected(inputId: string, draft: Date | null): boolean {
        const field = this.host.nativeElement.querySelector<HTMLInputElement>(`#${inputId}`);

        return draft === null && (field?.value.trim() ?? '') !== '';
    }
}
